// Reportings partenaires : logique pure de l'éditeur (mêmes règles que common/domain/model/partner_report.go).
export const TEXT_BLOCKS = ["intro", "analysis", "actions", "signature"];
export const isTextBlock = (type) => TEXT_BLOCKS.includes(type);

const LABELS = {
  intro: "Introduction", kpis: "Indicateurs clés", position: "Position sur le marché", top_products: "Top produits",
  vouchers: "Codes promo", analysis: "Analyse", actions: "Plan d'action", signature: "Signature",
  daily: "Évolution (jour / semaine / mois)", split: "Répartition", price: "Compétitivité prix", timing: "Délai clic → vente",
  share: "Place parmi les marques", models: "Modèles phares", intent: "Intention d'achat", share_history: "Part des clics sur 6 / 12 mois",
  distributors: "Ventes par distributeur",
};
export const blockLabel = (type) => LABELS[type] || type;

const STATUS = { draft: "Brouillon", sending: "Envoi en cours", sent: "Envoyé", failed: "Échec d'envoi" };
export const statusLabel = (s) => STATUS[s] || s;

export function canSend(report, config) {
  if (!config || !(config.to || []).length) return { ok: false, reason: "Aucun destinataire « À » configuré pour ce partenaire" };
  if (report.status === "sending") return { ok: false, reason: "Envoi déjà en cours" };
  if (!(report.subject || "").trim()) return { ok: false, reason: "Objet du mail vide" };
  const bad = (report.blocks || []).find((b) => !b.hidden && b.unavailable);
  if (bad) return { ok: false, reason: `Bloc « ${bad.title || blockLabel(bad.type)} » : données indisponibles — masque-le ou régénère` };
  return { ok: true, reason: "" };
}

export function moveBlock(blocks, index, dir) {
  const to = index + dir;
  if (to < 0 || to >= blocks.length) return blocks;
  const next = blocks.slice();
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

export const toggleHidden = (blocks, id) => blocks.map((b) => (b.id === id ? { ...b, hidden: !b.hidden } : b));
export const updateBlock = (blocks, id, patch) => blocks.map((b) => (b.id === id ? { ...b, ...patch } : b));

export function formatDelta(value, previous) {
  if (!previous) return value ? "nouveau" : "=";
  const pct = Math.round(((value - previous) / Math.abs(previous)) * 100);
  if (pct > 0) return `+${pct} %`;
  if (pct < 0) return `${pct} %`;
  return "=";
}

// Barre d'outils Quill : gras, italique, souligné, couleurs texte/fond, listes, liens, titres.
export const QUILL_MODULES = {
  toolbar: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ color: [] }, { background: [] }], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]],
};
export const QUILL_FORMATS = ["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"];

// Rythmes des rapports (l'hebdo a été retiré le 13/10/2026).
export const KINDS = [["monthly", "Mensuel"], ["quarterly", "Trimestriel"], ["yearly", "Annuel"]];
export const kindLabel = (k) => (KINDS.find(([id]) => id === k) || [k, k])[1];

// Périodes proposées : la plus récente complète d'abord (comme le robot).
export function periodOptions(kind, now = new Date()) {
  const out = [];
  if (kind === "quarterly") {
    const q = Math.floor(now.getMonth() / 3); // trimestre en cours (0-3), non terminé
    for (let i = 1; i <= 8; i++) {
      const n = now.getFullYear() * 4 + q - i;
      out.push(`${Math.floor(n / 4)}-Q${(n % 4) + 1}`);
    }
    return out;
  }
  if (kind === "yearly") {
    for (let i = 1; i <= 3; i++) out.push(String(now.getFullYear() - i));
    return out;
  }
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

// Écran de la liste des rapports (onglet, rythme, période) : mémorisé pour le retrouver en revenant d'un rapport
// ou d'une autre page de pikachu (demande d'Antoine, 13/10/2026). Stockage navigateur : simple confort.
const LIST_KEY = "partnerReports.list";

export function readListState(search, stored, now = new Date()) {
  const q = new URLSearchParams(search || "");
  let saved = {};
  try { saved = JSON.parse(stored || "{}") || {}; } catch (e) { saved = {}; }
  const pick = (k) => q.get(k) || saved[k];
  const type = pick("type") === "brand" ? "brand" : "merchant";
  const kind = KINDS.some(([id]) => id === pick("kind")) ? pick("kind") : "monthly";
  const options = periodOptions(kind, now);
  const period = pick("kind") === kind && /^\d{4}(-(\d{2}|Q[1-4]))?$/.test(pick("period") || "") ? pick("period") : options[0];
  return { type, kind, period };
}

export function loadListState(search) {
  let stored = null;
  try { stored = window.localStorage.getItem(LIST_KEY); } catch (e) { stored = null; }
  return readListState(search, stored);
}

export function saveListState(state) {
  try { window.localStorage.setItem(LIST_KEY, JSON.stringify(state)); } catch (e) { /* navigation privée : sans mémoire */ }
}

export const listQuery = ({ type, kind, period }) => `?type=${type}&kind=${kind}&period=${encodeURIComponent(period)}`;

// routeParam : react-router 5 rend les paramètres d'URL encore encodés (« alltricks%23fr ») ; on les décode une fois.
export function routeParam(v) {
  try { return decodeURIComponent(v || ""); } catch (e) { return v || ""; }
}

// Contenu texte du mail court (demande d'Antoine, 15/10/2026) : analyse + plan d'action, l'un ou l'autre.
// Pilote `emailVisible` des blocs analysis / actions (le PDF les garde tant qu'ils ne sont pas masqués).
export const MAIL_TEXT_CHOICES = [
  { value: "both", label: "Analyse + plan d'action" },
  { value: "analysis", label: "Analyse seule" },
  { value: "actions", label: "Plan d'action seul" },
];

const inMail = (b) => b && !b.hidden && b.emailVisible;

export function mailTextChoice(blocks) {
  const a = inMail((blocks || []).find((b) => b.type === "analysis"));
  const p = inMail((blocks || []).find((b) => b.type === "actions"));
  if (a && p) return "both";
  if (a) return "analysis";
  if (p) return "actions";
  return "";
}

export function setMailTextChoice(blocks, choice) {
  const want = { analysis: choice === "both" || choice === "analysis", actions: choice === "both" || choice === "actions" };
  return blocks.map((b) => {
    if (!(b.type in want)) return b;
    return want[b.type] ? { ...b, emailVisible: true, hidden: false } : { ...b, emailVisible: false };
  });
}

// Expéditeur du mail (15/10/2026) : un utilisateur pikachu actif (le back refuse tout autre adresse), Antoine par défaut.
export const DEFAULT_SENDER = "antoine.provost@therunningcollective.fr";

export function senderOptions(users) {
  return (users || [])
    .filter((u) => u && u.enable && (u.email || "").trim())
    .map((u) => ({ email: u.email.trim().toLowerCase(), login: u.login || "", label: `${[u.firstName, u.name].filter(Boolean).join(" ").trim() || u.email} · ${u.email.trim().toLowerCase()}` }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

// L'expéditeur n'est pas mis en copie de son propre mail (même règle que le back).
export const ccWithout = (cc, sender) => (cc || []).filter((c) => (c || "").trim().toLowerCase() !== (sender || "").trim().toLowerCase());
